import { getFrameLabel } from '@Constants/getFrameLabel';
import { Permission } from '@Constants/Permission.g';
import { useHasPermission } from '@Contexts/appContext';
import type { ApiCounter } from '@Model';
import {
    Card,
    EditFrameMode,
    EditFrameShell,
    FlexRow,
    FrameEditBase,
    Grid,
    GridColumns,
    type IFrameBodyProps,
    TextInput,
    isNullOrEmpty,
    SelectOption,
    SingleSelect,
    StyledH3,
    useFrameEditBaseNavigation,
    useGuiBinder,
    NumberInput,
    parseAsInt,
    DateInput,
} from '@synqo/frontend-component-library';
import { useParams } from 'react-router';

export interface FrameEditCounterBodyProps<TCounterType> {
    counterTypesItems: SelectOption<TCounterType>[];
    editPermission: Permission;
    deletePermission: Permission;
}

export interface FrameEditCounterProps<TCounterType> extends FrameEditCounterBodyProps<TCounterType> {
    apiResourcePath: string;
}

export function FrameEditCounter<TCounterType extends number>(
    frameEditCounterProps: FrameEditCounterProps<TCounterType>,
) {
    const hasPermission = useHasPermission();
    const { itemId } = useParams();
    const primaryKey = itemId ?? '';

    const frameEditBaseNavigationEvents = useFrameEditBaseNavigation();

    return (
        <FrameEditBase<ApiCounter<TCounterType>>
            apiResourcePath={frameEditCounterProps.apiResourcePath}
            renderer={(props) => <FrameEditCounterBody<TCounterType> {...props} {...frameEditCounterProps} />}
            key={primaryKey}
            primaryKey={primaryKey}
            embedded={false}
            canEdit={hasPermission(Permission.BillingCountersUpdate)}
            canCreate={hasPermission(Permission.BillingCountersCreate)}
            canDelete={hasPermission(Permission.BillingCountersDelete)}
            frameProps={{
                events: {
                    ...frameEditBaseNavigationEvents,
                },
            }}
        />
    );
}

function FrameEditCounterBody<TCounterType extends number>(
    props: IFrameBodyProps<ApiCounter<TCounterType>> & FrameEditCounterBodyProps<TCounterType>,
) {
    const hasPermission = useHasPermission();

    const apiCounter = props.data;
    const canEdit = hasPermission(props.editPermission) && props.mode !== EditFrameMode.View;

    const binder = useGuiBinder<ApiCounter<TCounterType>>({ ...props, canEdit });

    const isNumeric = apiCounter.is_numeric;

    function formatStartingValue(value: string) {
        const minLength = apiCounter.minimum_digits;
        const parsed = parseAsInt(value);
        return parsed.toString().padStart(minLength, '0');
    }

    const sampleValueFilled = !isNullOrEmpty(apiCounter.sample_value);

    return (
        <EditFrameShell
            headerProps={{
                label: getFrameLabel('Zähler', props.mode),
                resourceLabel: 'Zähler',
                ...props.events,
            }}
            bannerProps={{
                mode: props.mode,
                loading: props.stateQueueDepth > 0,
                ...props.events,
            }}
        >
            <Card name="CounterData" border={'withoutBorder'}>
                <Grid>
                    {!isNumeric && (
                        <GridColumns spans={[6, 6]}>
                            <TextInput {...binder.bindToProp('prefix_template')} label={'Präfix'} />
                            <NumberInput {...binder.bindToProp('minimum_digits')} label={'Mindestanzahl der Ziffern'} />
                        </GridColumns>
                    )}
                    <GridColumns spans={[6, 6, 12]}>
                        <TextInput
                            {...binder.bindToProp<string>(
                                'start_counter',
                                (v) => parseAsInt(v).toString(),
                                formatStartingValue,
                            )}
                            label={'Anfangswert'}
                        />

                        <DateInput type="day" {...binder.bindToProp('valid_from')} label={'Gültig ab'} />
                        <SingleSelect
                            items={props.counterTypesItems}
                            {...binder.bindToAutoConfirmed<TCounterType>('counter_type')}
                            label={'Zählertyp'}
                        />
                    </GridColumns>
                </Grid>
            </Card>
            {sampleValueFilled && (
                <Card name="preview">
                    <FlexRow align="center">
                        <StyledH3>Beispiel-Wert: {apiCounter.sample_value}</StyledH3>
                    </FlexRow>
                </Card>
            )}
        </EditFrameShell>
    );
}
